import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import type { JSX } from 'react';
import { cn } from '@/lib/ui/utils';
function createBadgeVariants(): ReturnType<typeof cva<{
  variant: {
    default: string;
    secondary: string;
    destructive: string;
    outline: string;
    ghost: string;
    link: string;
  };
}>> {
  return cva('inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-border-primary focus-visible:ring-[3px] focus-visible:ring-border-primary/50 aria-invalid:border-border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3', {
    variants: {
      variant: {
        default: 'bg-foreground-primary text-background-primary [a&]:hover:bg-foreground-primary/90',
        secondary: 'bg-background-tertiary text-foreground-primary [a&]:hover:bg-background-tertiary/90',
        destructive: 'bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
        outline: 'border-border-primary text-foreground-primary [a&]:hover:bg-background-tertiary [a&]:hover:text-foreground-primary',
        ghost: '[a&]:hover:bg-background-tertiary [a&]:hover:text-foreground-primary',
        link: 'text-foreground-primary underline-offset-4 [a&]:hover:underline'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  });
}
const badgeVariants: ReturnType<typeof createBadgeVariants> = createBadgeVariants();
function Badge(t0) {
  const $ = _c(13);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      asChild: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  const asChild = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot.Root : "span";
  let t3;
  if ($[5] !== className || $[6] !== variant) {
    t3 = cn(badgeVariants({
      variant
    }), className);
    $[5] = className;
    $[6] = variant;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== Comp || $[9] !== props || $[10] !== t3 || $[11] !== variant) {
    t4 = <Comp data-slot="badge" data-variant={variant} className={t3} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = t3;
    $[11] = variant;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export { Badge, badgeVariants };